/**
 * Единая статусная модель продукта.
 *
 * Домены (задачи, прогоны цепочек, кампании, модели, датасеты, фичи,
 * подключения) называют свои состояния по-разному, но глазами читаются они
 * одинаково — как расстояние до финала. Здесь доменный статус переводится
 * в роль, а роль уже красится токенами --status-* из src/styles/theme.css.
 *
 * Резолвер возвращает роль, а не CSS-класс: одно и то же решение красит и
 * бейдж, и точку в графе, и полосу прогресса.
 *
 * Новый статус добавляется сюда, а не в CSS раздела.
 */

/** Роли шкалы. Порядок — от «ещё не начиналось» к «финал». */
export const TONE = {
  /** Заведено, но ещё не в работе: создано, черновик, неизвестно. */
  NEUTRAL: 'neutral',
  /** Ждёт или приостановлено: в очереди, на паузе, частично. */
  WAIT: 'wait',
  /** Идёт прямо сейчас: выполняется, обучается, сборка, скачивание. */
  PROGRESS: 'progress',
  /** Здоровый исход или рабочее состояние: выполнено, успешно, активна. */
  GOOD: 'good',
  /** Ошибка — нужен человек. */
  BAD: 'bad',
  /** Выключено, отменено, пропущено, архив — выпадение из шкалы. */
  OFF: 'off',
};

export const TONES = Object.values(TONE);

/** Человеческие названия ролей — для каталога статусов и подсказок. */
export const TONE_LABELS = {
  [TONE.NEUTRAL]: 'Не в работе',
  [TONE.WAIT]: 'Ждёт',
  [TONE.PROGRESS]: 'Идёт',
  [TONE.GOOD]: 'Хорошо',
  [TONE.BAD]: 'Ошибка',
  [TONE.OFF]: 'Вне шкалы',
};

const norm = (status) => String(status ?? '').trim().toLowerCase();

/**
 * Слова, которые означают одно и то же во всех доменах. Используется как
 * запасной разбор, когда доменный резолвер не знает статус: бэкенды заводят
 * новые состояния быстрее, чем фронт их описывает, и лучше показать
 * правдоподобный цвет, чем серый «не знаю».
 */
function fallbackTone(status) {
  const key = norm(status);
  if (!key) return TONE.NEUTRAL;

  if (key.includes('error') || key.includes('fail')) return TONE.BAD;
  if (key.includes('cancel') || key.includes('skip')) return TONE.OFF;
  if (key.includes('archiv') || key.includes('delet') || key.includes('disabl')) return TONE.OFF;
  if (key.includes('run') || key.includes('progress') || key.includes('load')) return TONE.PROGRESS;
  if (key.includes('queue') || key.includes('wait') || key.includes('pend')) return TONE.WAIT;
  if (key.includes('pause') || key.includes('block')) return TONE.WAIT;
  if (key.includes('done') || key.includes('success') || key.includes('complete')) return TONE.GOOD;
  if (key.includes('active') || key.includes('ready')) return TONE.GOOD;
  if (key.includes('draft') || key.includes('creat')) return TONE.NEUTRAL;

  return TONE.NEUTRAL;
}

/** Собирает резолвер из таблицы соответствий с общим запасным разбором. */
function resolver(map) {
  return (status) => {
    const key = norm(status);
    if (key in map) return map[key];
    return fallbackTone(key);
  };
}

// ============================================================
// Задачи dtcore (интеграции, расчёт фич)
// ============================================================

export const taskTone = resolver({
  created: TONE.NEUTRAL,
  started: TONE.NEUTRAL,
  queued: TONE.WAIT,
  paused: TONE.WAIT,
  blocked: TONE.WAIT,
  running: TONE.PROGRESS,
  done: TONE.GOOD,
  'done and vectorization ok': TONE.GOOD,
  'zero conversions today': TONE.GOOD,
  error: TONE.BAD,
  cancel: TONE.OFF,
  cancelled: TONE.OFF,
  canceled: TONE.OFF,
});

// ============================================================
// Интеграции
// ============================================================

export const integrationTone = resolver({
  active: TONE.GOOD,
  created: TONE.NEUTRAL,
  paused: TONE.WAIT,
  error: TONE.BAD,
});

// ============================================================
// Цепочки: сама цепочка, прогон, шаг прогона
// ============================================================

export const chainTone = resolver({
  active: TONE.GOOD,
  draft: TONE.NEUTRAL,
  disabled: TONE.OFF,
  deleted: TONE.OFF,
});

export const chainRunTone = resolver({
  running: TONE.PROGRESS,
  partial: TONE.WAIT,
  success: TONE.GOOD,
  failed: TONE.BAD,
  cancelled: TONE.OFF,
});

export const chainStepTone = resolver({
  waiting: TONE.WAIT,
  queued: TONE.WAIT,
  running: TONE.PROGRESS,
  success: TONE.GOOD,
  failed: TONE.BAD,
  skipped: TONE.OFF,
  cancelled: TONE.OFF,
});

// ============================================================
// Рекомендательные кампании: кампания, модель, прогон обучения
// ============================================================

// У кампании статус отвечает на один вопрос: отдаём ли мы рекомендации.
// «Активна» — только когда есть готовая версия; свежесозданная кампания
// зеленеть не должна. Прогресс и ошибки живут в версии и её прогонах.
export const campaignTone = resolver({
  draft: TONE.NEUTRAL,
  preparing: TONE.PROGRESS,
  active: TONE.GOOD,
  paused: TONE.WAIT,
  // Статусы до перехода на новую модель — встречаются, пока не доехала миграция.
  configuring: TONE.NEUTRAL,
  training: TONE.PROGRESS,
  deploying: TONE.PROGRESS,
  failed: TONE.BAD,
  archived: TONE.OFF,
});

// Версия модели — вторая, независимая вертикаль: created → training → serving
// → ready. Именно она отвечает на вопрос «что происходит прямо сейчас».
export const campaignVersionTone = resolver({
  created: TONE.WAIT,
  training: TONE.PROGRESS,
  serving: TONE.PROGRESS,
  ready: TONE.GOOD,
  failed: TONE.BAD,
  // Старые значения версий до миграции.
  trained: TONE.NEUTRAL,
  deployed: TONE.GOOD,
  cancelled: TONE.OFF,
});

// Контейнер моделей кампании. Это не конвейер: его статус ничего не говорит о
// том, где сейчас обучение, — за этим ходят в версию. Резолвер остаётся для
// справочника статусов и старых экранов.
export const campaignModelTone = resolver({
  draft: TONE.NEUTRAL,
  queued: TONE.WAIT,
  training: TONE.PROGRESS,
  // Обучена, но не выведена в бой — рабочее состояние, а не финал.
  trained: TONE.NEUTRAL,
  deployed: TONE.GOOD,
  failed: TONE.BAD,
  archived: TONE.OFF,
});

export const trainingRunTone = resolver({
  pending: TONE.WAIT,
  queued: TONE.WAIT,
  running: TONE.PROGRESS,
  cancelling: TONE.WAIT,
  succeeded: TONE.GOOD,
  success: TONE.GOOD,
  completed: TONE.GOOD,
  failed: TONE.BAD,
  cancelled: TONE.OFF,
});

// ============================================================
// Датасеты: сам датасет и прогон сборки
// ============================================================

export const datasetTone = resolver({
  draft: TONE.NEUTRAL,
  building: TONE.PROGRESS,
  active: TONE.GOOD,
  failed: TONE.BAD,
  archived: TONE.OFF,
});

export const datasetBuildTone = resolver({
  queued: TONE.WAIT,
  running: TONE.PROGRESS,
  success: TONE.GOOD,
  failed: TONE.BAD,
  error: TONE.BAD,
});

// ============================================================
// Feature store: фичи и события
// ============================================================

export const featureTone = resolver({
  active: TONE.GOOD,
  paused: TONE.WAIT,
  deprecated: TONE.WAIT,
  archived: TONE.OFF,
  merged: TONE.OFF,
});

// ============================================================
// Модели инференса: состояние загрузки в память (админка)
// ============================================================

export const modelLoadTone = resolver({
  not_loaded: TONE.NEUTRAL,
  on_disk: TONE.NEUTRAL,
  downloading: TONE.PROGRESS,
  loading: TONE.PROGRESS,
  ready: TONE.GOOD,
  error: TONE.BAD,
});

/** Каталожная модель: active / inactive / maintenance / deprecated. */
export const modelCatalogTone = resolver({
  active: TONE.GOOD,
  inactive: TONE.OFF,
  maintenance: TONE.WAIT,
  deprecated: TONE.WAIT,
});

// ============================================================
// Подключения и внешние сервисы
// ============================================================

export const connectionTone = resolver({
  active: TONE.GOOD,
  inactive: TONE.OFF,
});

export const telegramTone = resolver({
  bound: TONE.GOOD,
  disabled: TONE.OFF,
});

// ============================================================
// Сущности с общим жизненным циклом:
// источники, хранилища, проекты, API-ключи, коннекторы
// ============================================================

export const entityTone = resolver({
  active: TONE.GOOD,
  created: TONE.NEUTRAL,
  draft: TONE.NEUTRAL,
  unknown: TONE.NEUTRAL,
  pause: TONE.WAIT,
  paused: TONE.WAIT,
  // Раньше «неактивно» заливалось красным и читалось аварией.
  // Это не ошибка, а выключенное состояние.
  inactive: TONE.OFF,
  disabled: TONE.OFF,
  archived: TONE.OFF,
  error: TONE.BAD,
});

// ============================================================
// Мониторинг качества: дни, пульс таблицы, алерты, задачи
// ============================================================

/**
 * Статус дня события. pending — не ошибка, а «день ещё не закрыт»:
 * серый нейтральный, а не красный (иначе каждое утро выглядит аварией).
 */
export const monitorDayTone = resolver({
  ok: TONE.GOOD,
  warn: TONE.WAIT,
  fail: TONE.BAD,
  pending: TONE.NEUTRAL,
  // проверки за день не было — дырка в календаре, а не результат
  none: TONE.OFF,
});

/** Статус дня таблицы (пульс): строки за сутки были или нет. */
export const tableDayTone = resolver({
  ok: TONE.GOOD,
  pending: TONE.NEUTRAL,
  no_data: TONE.BAD,
});

/** Состояние алерта: открыт, взят в работу, закрылся сам. */
export const alertStatusTone = resolver({
  open: TONE.BAD,
  acknowledged: TONE.PROGRESS,
  resolved: TONE.GOOD,
});

/** Важность алерта. */
export const alertSeverityTone = resolver({
  critical: TONE.BAD,
  warning: TONE.WAIT,
  info: TONE.NEUTRAL,
});

/** Задача мониторинга: очередь разбирает планировщик раз в минуту. */
export const monitoringRunTone = resolver({
  created: TONE.NEUTRAL,
  queued: TONE.WAIT,
  running: TONE.PROGRESS,
  success: TONE.GOOD,
  failed: TONE.BAD,
});
